import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from '@/context/AuthContext';
import { AppProvider } from '@/context/AppContext';
import PrivateRoute from '@/components/shared/PrivateRoute';
import LoginScreen from '@/components/auth/LoginScreen';
import AccessDenied from '@/components/auth/AccessDenied';
import Dashboard from '@/components/dashboard/Dashboard';
import ExpenseList from '@/components/expenses/ExpenseList';
import AddExpenseForm from '@/components/expenses/AddExpenseForm';
import PlannedExpenses from '@/components/planned/PlannedExpenses';
import BudgetScreen from '@/components/budget/BudgetScreen';
import Analytics from '@/components/analytics/Analytics';
import Settings from '@/components/settings/Settings';
import TodoScreen from '@/components/todos/TodoScreen';
import ErrorBoundary from '@/components/shared/ErrorBoundary';

function OfflineBanner() {
  const [offline, setOffline] = useState(!navigator.onLine);
  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline  = () => setOffline(false);
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);
  if (!offline) return null;
  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-white text-xs
                    font-semibold text-center py-1 px-3 shadow">
      📡 You're offline — expenses will sync when back online
    </div>
  );
}

/** Request browser/OS notification permission once (for Android 13+ prompt) */
function useNotificationPermission() {
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => {});
    }
  }, []);
}

export default function App() {
  useNotificationPermission();
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppProvider>
            <OfflineBanner />
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginScreen />} />
              <Route path="/access-denied" element={<AccessDenied />} />

              {/* Protected routes */}
              <Route element={<PrivateRoute />}>
                <Route path="/" element={<Dashboard />} />
                <Route path="/expenses" element={<ExpenseList />} />
                <Route path="/expenses/add" element={<AddExpenseForm />} />
                <Route path="/expenses/edit/:id" element={<AddExpenseForm />} />
                <Route path="/planned" element={<PlannedExpenses />} />
                <Route path="/budget" element={<BudgetScreen />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/todos" element={<TodoScreen />} />
                <Route path="/settings" element={<Settings />} />
              </Route>

              {/* Fallback */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AppProvider>
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
